import { CoinsVal } from "@components/icons/coins";
import { ResponsiveSelect } from "@components/responsive-select";
import UpgradeNeeded from "@components/upgrade-needed";
import { ImagesType } from "@features/images/type";
import { useImage } from "@features/images/use-images";
import { useSavedState } from "@features/utils/use-saved-state";
import { Button, Heading, Text, TextArea } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface StudioBackgroundProps {
  image: ImagesType;
  onAction: (action: string) => void;
}

export const StudioBackground = ({
  image,
  onAction,
}: StudioBackgroundProps) => {
  const { t } = useTranslation();
  const { background } = useImage(image.id);
  const [prompt, setPrompt] = useState("");
  const [count, setCount] = useSavedState("background-count", 1);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleRemoveBackground = async () => {
    setIsProcessing(true);
    try {
      await background();
      onAction("background-removed");
    } catch (error) {
      console.error("Failed to remove background:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReplaceBackground = async () => {
    if (!prompt.trim()) {
      return;
    }

    setIsProcessing(true);
    try {
      await background(undefined, prompt.trim() || undefined, count);
      onAction("background-replaced");
    } catch (error) {
      console.error("Failed to replace background:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <Heading size="3">
        {t("studio.actions.background.remove_or_change")}
      </Heading>

      <div className="space-y-3">
        <div>
          <Text size="2" weight="medium" as="label" className="block mb-2">
            {t("studio.actions.background.remove_background")}
          </Text>
          <Text size="2" color="gray" className="block mb-3">
            {t("studio.actions.background.remove_description")}
          </Text>
          <Button
            onClick={handleRemoveBackground}
            disabled={isProcessing}
            loading={isProcessing}
            variant="outline"
            className="w-full"
          >
            {t("studio.actions.background.remove_background")}
          </Button>
        </div>

        {true && (
          <div className="border-t pt-4">
            <Text size="2" weight="medium" as="label" className="block mb-2">
              {t("studio.actions.background.replace_background")}
            </Text>
            <Text size="2" color="gray" className="block mb-3">
              {t("studio.actions.background.replace_description")}
            </Text>

            <div className="space-y-3">
              <div>
                <Text size="1" color="gray" className="block mb-1">
                  {t("studio.actions.background.new_background_description")}
                </Text>
                <TextArea
                  placeholder={t(
                    "studio.actions.background.placeholder_description"
                  )}
                  value={prompt}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setPrompt(e.target.value)
                  }
                  rows={3}
                />
              </div>

              <div className="mt-2 flex space-x-2 w-full">
                <ResponsiveSelect
                  size="2"
                  value={count.toString()}
                  onChange={(value) => setCount(Number(value))}
                  items={[
                    { value: "1", label: "1" },
                    { value: "2", label: "2" },
                    { value: "3", label: "3" },
                    { value: "4", label: "4" },
                  ]}
                  renderTrigger={() => (
                    <span className="text-xs uppercase text-gray-500">
                      {count}
                    </span>
                  )}
                />
                <div>
                  <UpgradeNeeded minimumCredits={2 * count}>
                    <Button
                      onClick={handleReplaceBackground}
                      disabled={isProcessing || !prompt.trim()}
                      loading={isProcessing}
                      className="grow"
                    >
                      {t("studio.actions.background.replace_background")}{" "}
                      <CoinsVal val={2 * count} />
                    </Button>
                  </UpgradeNeeded>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
